import React from "react";
import { Box, Button, Typography } from "@mui/material";

// Catches a failed lazy-route load and recovers.
//
// THE FAILURE THIS FIXES: every deploy renames the code-split chunks. A tab
// left open across a deploy still asks for the OLD chunk name. Until now the
// server answered that with index.html and HTTP 200, so the browser tried to
// run HTML as JavaScript, the lazy import rejected, and -- with no error
// boundary anywhere in the app -- React unmounted everything and left a BLANK
// PAGE. No error, no request, nothing to look at. Client Settings did this for
// admins who keep the CRM open all day and had never opened that tab before.
//
// The server now 404s missing assets so this is a recognisable ChunkLoadError.
// Here we reload ONCE to pick up the new index.html, guarded so a genuinely
// broken build cannot put the app in a reload loop.
//
// This only covers a chunk that fails while the app is RUNNING. When the main
// bundle itself 404s (a tab holding an index.html from the previous build —
// `404 /static/js/main.d3ac5f7f.js`, 2026-09-07) this component never loads;
// the inline boot guard at the top of deploy/index.html catches that
// one. It reloads on the same key and within the same window, so the two of
// them share a single reload budget instead of taking turns.
const RELOAD_KEY = "phieg.chunkReloadAt";
const RELOAD_WINDOW_MS = 30000;

const isChunkError = (error) => {
  const msg = `${error?.name || ""} ${error?.message || ""}`;
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk .* failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    // The HTML-served-as-JS case, before the server fix reaches every instance.
    /Unexpected token '<'/.test(msg)
  );
};

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkError(error)) return;
    // Reload once. If we already reloaded moments ago the new build is not the
    // problem, so show the message instead of looping.
    let last = 0;
    try { last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0); } catch { /* private mode */ }
    if (Date.now() - last < RELOAD_WINDOW_MS) return;
    try { window.sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* ignore */ }
    this.setState({ reloading: true });
    window.location.reload();
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkError(error);
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {reloading ? "Updating to the latest version…" : chunk ? "This page needs a refresh" : "Something went wrong on this page"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, opacity: 0.75 }}>
          {chunk
            ? "The app was updated while this tab was open, so part of it could not load."
            : String(error?.message || "").slice(0, 200)}
        </Typography>
        {!reloading && (
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
        )}
      </Box>
    );
  }
}
